import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * NiiCoverageStrip — Sep 2026
 *
 * A compact "trailing coverage ratio + sparkline" strip used inside the
 * Valuation Matrix CEF-bypass slot. Some CEFs (credit funds, CEF-of-
 * BDCs, senior-loan CEFs) actually DO have meaningful SEC-reported
 * distribution coverage, and hiding it means users only see the
 * Z-score angle. When `sec_coverage.status === 'ok'` for a CEF, this
 * strip gives them the second data point.
 *
 * Deliberately factored small so it also renders cleanly if a future
 * asset class needs a "coverage vs distributions" visual — same
 * defensive contract as BdcNavGauge: returns null when data is
 * missing/malformed rather than breaking the parent card.
 *
 * Props:
 *   coverage: stock.sec_coverage object (must have status === 'ok')
 *   label:    header label, defaults to 'NII Coverage'
 *   testIdPrefix: data-testid prefix for querying in tests
 */
function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function NiiCoverageStrip({ coverage, label = 'NII Coverage', testIdPrefix = 'coverage' }) {
  if (!coverage || typeof coverage !== 'object' || coverage.status !== 'ok') return null;
  const covRatio = safeNum(coverage.ratio_current);
  if (covRatio == null || covRatio < 0) return null;

  const covTrend = safeNum(coverage.trend_pct);
  const covLatestEnd = coverage.latest_quarter_end || null;
  const covQuarters = Array.isArray(coverage.quarters) ? coverage.quarters : [];

  const sparkPoints = covQuarters
    .map((q) => ({ end: q?.end, ratio: safeNum(q?.ratio) }))
    .filter((p) => p.ratio != null && p.ratio >= 0);

  return (
    <div data-testid={`${testIdPrefix}-strip`}>
      <div className="flex items-center justify-between flex-wrap gap-1.5 mb-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">
            {label}
          </span>
          <span
            className="text-xs font-mono font-bold text-foreground tabular-nums"
            data-testid={`${testIdPrefix}-value`}
            title={covLatestEnd ? `Trailing quarter ended ${covLatestEnd}` : undefined}
          >
            {covRatio.toFixed(2)}×
          </span>
        </div>
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 cursor-help ${
            covRatio >= 1.05
              ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 ring-emerald-400/40'
              : covRatio >= 1.0
                ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/5 ring-emerald-400/30'
                : covRatio >= 0.90
                  ? 'text-amber-700 dark:text-amber-300 bg-amber-500/10 ring-amber-400/40'
                  : 'text-rose-700 dark:text-rose-300 bg-rose-500/15 ring-rose-500/40'
          }`}
          data-testid={`${testIdPrefix}-bucket`}
          title={
            covRatio >= 1.0
              ? 'SEC-reported distribution coverage is ≥ 1.0× — the distribution is being earned from investment income.'
              : covRatio >= 0.90
                ? 'Coverage is slightly under 1.0×. Some of the distribution is coming from realized gains or return of capital.'
                : 'Coverage is well below 1.0× — the distribution is materially subsidized by realized gains, option premium, or return of capital.'
          }
        >
          {covRatio >= 1.0
            ? <><CheckCircle2 className="w-3 h-3" /> Sustainable <span aria-hidden className="opacity-70">ⓘ</span></>
            : covRatio >= 0.90
              ? <>Marginal <span aria-hidden className="opacity-70">ⓘ</span></>
              : <><AlertTriangle className="w-3 h-3" /> At Risk <span aria-hidden className="opacity-70">ⓘ</span></>}
        </span>
      </div>

      <div className="relative h-2 rounded-full bg-muted/40 overflow-hidden" data-testid={`${testIdPrefix}-bar`}>
        <div
          className={`absolute inset-y-0 left-0 ${
            covRatio >= 1.0
              ? 'bg-emerald-500/70'
              : covRatio >= 0.90 ? 'bg-amber-500/70' : 'bg-rose-500/70'
          }`}
          style={{ width: `${Math.max(2, Math.min(100, (covRatio / 1.5) * 100))}%` }}
        />
        <div className="absolute top-0 bottom-0 w-px bg-foreground/60" style={{ left: `${(1.0 / 1.5) * 100}%` }} />
      </div>
      <div className="flex items-center justify-between text-[9px] font-mono text-muted-foreground mt-0.5">
        <span>0.0×</span>
        <span className="font-bold text-foreground">1.0×</span>
        <span>1.5×</span>
      </div>

      {sparkPoints.length >= 2 && (
        <div className="mt-1 flex items-end gap-0.5 h-6" title="Trailing quarterly coverage" data-testid={`${testIdPrefix}-sparkline`}>
          {sparkPoints.map((p, i) => {
            const h = Math.max(6, Math.min(100, (p.ratio / 1.5) * 100));
            const color = p.ratio >= 1.0 ? 'bg-emerald-500/70' : p.ratio >= 0.90 ? 'bg-amber-500/70' : 'bg-rose-500/70';
            return (
              <div
                key={p.end || i}
                className={`flex-1 min-w-[6px] rounded-t ${color}`}
                style={{ height: `${h}%` }}
                title={`${p.end || `Q${i + 1}`}: ${p.ratio.toFixed(2)}×`}
              />
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground italic leading-relaxed mt-1">
        {covTrend != null && (
          <>
            Trend{' '}
            <span className={`font-semibold ${covTrend >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
              {covTrend >= 0 ? '+' : ''}{covTrend.toFixed(1)}%
            </span>
            {sparkPoints.length >= 2 && <> over {sparkPoints.length}Q</>}
            {covLatestEnd && <> · latest Q ended {covLatestEnd}</>}
          </>
        )}
        {covTrend == null && covLatestEnd && <>Latest Q ended {covLatestEnd}.</>}
      </p>
    </div>
  );
}
